import React, { useState } from "react";
import EastIcon from "@mui/icons-material/East";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import sealImage from "../../../../assets/home-page/authorized-interior-designer-seal.png";

export const MainContentSection = () => {
  const [expanded, setExpanded] = useState("panel1");

  const handleAccordionChange =
    (panel: any) => (event: any, isExpanded: any) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Data for the three feature cards
  const featureCards = [
    {
      title: "Empowering",
      description: "Great Home Design is our passion.",
      iconSrc: "https://c.animaapp.com/m92ydecaGfSTBy/img/seal.svg",
    },
    {
      title: "Curating",
      description: "Local interior designers & decorators is our business.",
      iconSrc: "https://c.animaapp.com/m92ydecaGfSTBy/img/seal.svg",
    },
    {
      title: "Connecting",
      description: "Meaningful design relationships is what sets us apart.",
      iconSrc: "https://c.animaapp.com/m92ydecaGfSTBy/img/seal.svg",
    },
  ];

  // Data for accordion sections
  const accordionData = [
    {
      id: "panel1",
      title: "Decisions Backed By The Seal",
      content:
        "At Home Design Guild, we're here to help you navigate the sea of designer choices with the most valuable compasses there are: research and facts. That's why, when a designer gets certified with The Guild Seal, they have earned it. By hiring a designer with our seal, you can trust that you're making a wise investment in your home's aesthetic and functionality.  Don't risk your project — match with a designer who meets the highest standards in the industry.",
    },
    {
      id: "panel2",
      title: "Our Vetting Process",
      content:
        "When choosing a designer for your home, it's essential to ensure you're working with the best partner for you. Designers who hold The Guild Seal have been thoroughly vetted and qualified, guaranteeing their expertise and commitment to excellence. Not every designer that applies for the Seal receives it. Our recommended designers also fill out a detailed profile to enable us to match not only your preferences but theirs - ensuring a great fit!",
    },
    {
      id: "panel3",
      title: "Our Guarantee",
      content:
        "We  stand behind who bears The Guild Seal and our matching process — so much so, that if a design project goes sour, we'll help you mediate a solution. Not only can you be confident in your choice, but also in having a partner with you every step of the journey.",
    },
  ];

  return (
    <Box>
      {/* Hero Section with Background */}
      <Box
        sx={{
          background: `url(https://c.animaapp.com/m92ydecaGfSTBy/img/cta.png) 50% 50% / cover, linear-gradient(0deg, rgba(112,107,96,1) 0%, rgba(112,107,96,1) 100%)`,
          padding: { xs: 4, md: 8 },
          width: "100%",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left empty space for image that's in the background */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: { xs: 200, md: 444 }, borderRadius: 2 }} />
            </Grid>

            {/* Right content area */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "#d22268",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Typography variant="overline" color="white" gutterBottom>
                    OUR MISSION
                  </Typography>
                  <Typography
                    variant="h4"
                    color="white"
                    gutterBottom
                    sx={{ fontWeight: 700, fontFamily: "'Jost', Helvetica" }}
                  >
                    Behind Every Inspired Home Design Is A Great Relationship
                  </Typography>
                  <Typography variant="body1" color="white">
                    We exist to bridge the gap between homeowners and interior
                    design professionals. We serve as a trusted platform that
                    connects homeowners with vetted designers and decorators.
                    Our goal is to make the process of finding a design
                    professional and understanding the process as seamless as
                    possible, ensuring homeowners can easily find the right
                    expert to bring their vision to life.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Three feature cards */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={4}
            sx={{ mt: 4, width: "100%" }}
          >
            {featureCards.map((card, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  bgcolor: "white",
                  borderRadius: 3, // 12px
                  p: 3,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)", // Subtle, soft shadow
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box
                    component="img"
                    src={card.iconSrc}
                    alt="Seal icon"
                    sx={{ width: 44, height: 44 }}
                  />
                  <Typography
                    variant="h6"
                    color="text.primary"
                    fontWeight="bold"
                  >
                    {card.title}
                  </Typography>
                </Stack>
                <Typography variant="body1">{card.description}</Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* The Seal Section */}
      <Container maxWidth="lg" sx={{ mt: 12, mb: 12 }}>
        <Grid container spacing={4} id="the-seal">
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <Typography
              variant={"display2" as any}
              component="h2"
              sx={{ mb: "16px" }}
              color="#d22268"
              gutterBottom
            >
              THE SEAL
            </Typography>
            <Box
              component="img"
              src={sealImage}
              alt="The Guild Seal"
              sx={{
                width: "100%",
                maxWidth: 500,
                height: "auto",
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ maxWidth: 488 }}>
              <Box component="span" fontWeight="semibold">
                The Guild Seal
              </Box>{" "}
              is your assurance of a trusted, vetted interior designer—backed by
              expertise, integrity, and a commitment to beautiful results.
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                endIcon={<EastIcon />}
                sx={{
                  borderRadius: 4,
                  p: 2,
                  borderColor: "#d22268",
                  color: "#d22268",
                }}
              >
                Ask For The Seal
              </Button>
            </Box>
          </Grid>

          {/* Right column - Accordion */}
          <Grid item xs={12} md={6}>
            {accordionData.map((item, index) => (
              <Accordion
                key={item.id}
                expanded={expanded === item.id}
                onChange={handleAccordionChange(item.id)}
                elevation={0}
                sx={{
                  borderTop: index < accordionData.length - 1 ? 0 : 1,
                  borderBottom: index < accordionData.length - 1 ? 0 : 1,
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 3 }}
                >
                  <Typography
                    variant="h6"
                    color="#d22268"
                    fontWeight="semibold"
                  >
                    {item.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary">
                    {item.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        </Grid>
      </Container>

      {/* Get In Touch Section */}
      <Box sx={{ bgcolor: "white", py: 6, textAlign: "center" }}>
        <Container maxWidth="md">
          <Typography
            variant={"display2" as any}
            component="h2"
            sx={{ mb: "16px" }}
            color="#d22268"
            gutterBottom
          >
            GET IN TOUCH
          </Typography>
          <Typography
            variant="h5"
            fontWeight="regular"
            color="text.primary"
            sx={{ mb: 4 }}
          >
            We'd love to hear from you. Here's how you can reach us.
          </Typography>
        </Container>
      </Box>

      {/* Contact Information Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        {/* Contact Form */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 675,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "relative",
              flexShrink: "0",
              boxSizing: "border-box",
              marginTop: "20px",
              minHeight: 900,
            }}
          >
            <iframe
              src="https://api.leadconnectorhq.com/widget/form/tAuIu7FvQ2oZSigmg5c9"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: 900,
                borderRadius: "16px",
              }}
              id="inline-tAuIu7FvQ2oZSigmg5c9"
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="HDG-General-Contact-Form"
              data-height="905"
              data-layout-iframe-id="inline-tAuIu7FvQ2oZSigmg5c9"
              data-form-id="tAuIu7FvQ2oZSigmg5c9"
              title="HDG-General-Contact-Form"
            />
            <script src="https://link.msgsndr.com/js/form_embed.js"></script>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
